// src/components/International.js
import React from "react";
import styled from "styled-components";
import {
  FaGlobe,
  FaUsers,
  FaGraduationCap,
  FaBookOpen,
  FaCertificate,
  FaTools,
  FaChartLine,
  FaHeadset,
} from "react-icons/fa";
import theme from "../styles/theme";

const InternationalSection = styled.section`
  padding: ${theme.spacing[24]} ${theme.spacing[4]};
  background: ${theme.gradients.hero};
  color: white;
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(
      circle at 20% 30%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(16, 185, 129, 0.1) 0%,
      transparent 50%
    );
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[6]};
  position: relative;
  z-index: 2;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing[4]};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[20]};
`;

const SectionTitle = styled.h2`
  font-size: clamp(${theme.typography.fontSize['2xl']}, 4vw, ${theme.typography.fontSize['4xl']});
  color: white;
  margin-bottom: ${theme.spacing[4]};
  font-weight: ${theme.typography.fontWeight.bold};
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[8]};
  margin-bottom: ${theme.spacing[20]};
`;

const StatCard = styled.div`
  text-align: center;
  background: ${theme.glass.background};
  backdrop-filter: ${theme.glass.backdropFilter};
  border: ${theme.glass.border};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[8]};
  transition: all ${theme.animation.normal} ease;

  &:hover {
    transform: translateY(-5px);
    background: ${theme.glass.backgroundStrong};
  }
`;

const StatIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${theme.gradients.primaryLight};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.typography.fontSize['2xl']};
  margin: 0 auto ${theme.spacing[4]};
`;

const StatNumber = styled.div`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: white;
  margin-bottom: ${theme.spacing[2]};
  font-family: ${theme.typography.fontFamily.heading};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.8);
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[16]};
  margin-bottom: ${theme.spacing[16]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[12]};
  }
`;

const ContentBlock = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: ${theme.glass.backdropFilter};
  border: ${theme.glass.border};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[10]};
`;

const BlockTitle = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  color: white;
  margin-bottom: ${theme.spacing[6]};
  font-weight: ${theme.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};

  svg {
    color: ${theme.accent.emerald[300]};
  }
`;

const BlockContent = styled.div`
  color: rgba(255, 255, 255, 0.95);
  line-height: ${theme.typography.lineHeight.relaxed};
  
  p {
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: ${theme.spacing[4]};
  }
`;

const ServicesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing[4]} 0;
  display: grid;
  gap: ${theme.spacing[3]};
`;

const ServiceItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.95);
  font-weight: ${theme.typography.fontWeight.medium};

  &::before {
    content: "•";
    color: ${theme.accent.emerald[300]};
    font-weight: bold;
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const TrainingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[8]};
`;

const TrainingCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  transition: all ${theme.animation.normal} ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const TrainingIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${theme.gradients.accent};
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.typography.fontSize.xl};
  margin-bottom: ${theme.spacing[4]};
`;

const TrainingTitle = styled.h4`
  font-size: ${theme.typography.fontSize.lg};
  color: white;
  margin-bottom: ${theme.spacing[3]};
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const TrainingDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.9);
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const CTAButton = styled.button`
  background: ${theme.gradients.accent};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.animation.normal} ease;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
  display: block;
  margin: ${theme.spacing[12]} auto 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
  }
`;

const stats = [
  {
    icon: <FaGlobe />,
    number: "50+",
    label: "Countries Served"
  },
  {
    icon: <FaCertificate />,
    number: "500+",
    label: "Successful Accreditations"
  },
  {
    icon: <FaUsers />,
    number: "1000+",
    label: "Professionals Trained"
  },
  {
    icon: <FaChartLine />,
    number: "98%",
    label: "Client Success Rate"
  },
];

const trainingPrograms = [
  {
    icon: <FaBookOpen />,
    title: "Standard Interpretation",
    description: "Deep-dive workshops on ISO 17025, ISO 17020, and AISC requirements with real-world application examples."
  },
  {
    icon: <FaTools />,
    title: "Implementation Support",
    description: "Step-by-step guidance for implementing quality management systems and achieving compliance."
  },
  {
    icon: <FaGraduationCap />,
    title: "Internal Auditor Certification",
    description: "Comprehensive training programs to develop internal audit capabilities and maintain ongoing compliance."
  },
  {
    icon: <FaHeadset />,
    title: "Ongoing Support",
    description: "Continuous professional development and technical support to ensure sustained accreditation success."
  },
];

const International = () => {
  return (
    <InternationalSection id="international">
      <BackgroundPattern />
      
      <Container>
        <SectionHeader>
          <SectionTitle>Serving Clients Worldwide</SectionTitle>
          <SectionSubtitle>
            Global accreditation excellence with expert ISO 17025, ISO 17020, and AISC consulting. 
            Our international team delivers comprehensive accreditation support across all industries and markets.
          </SectionSubtitle>
        </SectionHeader>

        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard key={index}>
              <StatIcon>{stat.icon}</StatIcon>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>

        <ContentGrid>
          <ContentBlock>
            <BlockTitle>
              <FaGlobe />
              International Reach
            </BlockTitle>
            <BlockContent>
              <p>
                Our global network of accreditation experts provides comprehensive support 
                across multiple time zones and regulatory environments. We understand the 
                nuances of international standards implementation and help organizations 
                navigate complex multi-jurisdictional requirements.
              </p>
              <ServicesList>
                <ServiceItem>Multi-country regulatory compliance</ServiceItem>
                <ServiceItem>Cross-border certification support</ServiceItem>
                <ServiceItem>International accreditation body liaison</ServiceItem>
                <ServiceItem>Global best practice implementation</ServiceItem>
                <ServiceItem>24/7 international support network</ServiceItem>
              </ServicesList>
            </BlockContent>
          </ContentBlock>

          <ContentBlock>
            <BlockTitle>
              <FaUsers />
              Client Success Stories
            </BlockTitle>
            <BlockContent>
              <p>
                From small laboratories to multinational corporations, our clients span 
                diverse industries including aerospace, automotive, construction, medical 
                devices, and environmental testing. We've successfully guided organizations 
                through initial accreditation and ongoing maintenance across six continents.
              </p>
              <ServicesList>
                <ServiceItem>Testing and calibration laboratories</ServiceItem>
                <ServiceItem>Inspection and certification bodies</ServiceItem>
                <ServiceItem>Structural steel fabricators</ServiceItem>
                <ServiceItem>Manufacturing quality systems</ServiceItem>
                <ServiceItem>Regulatory compliance organizations</ServiceItem>
              </ServicesList>
            </BlockContent>
          </ContentBlock>
        </ContentGrid>

        <ContentBlock>
          <BlockTitle>
            <FaGraduationCap />
            Training & Development Programs
          </BlockTitle>
          <BlockContent>
            <p>
              Our comprehensive training portfolio ensures your team has the knowledge and 
              skills needed for successful accreditation implementation and maintenance. 
              All programs can be delivered in-person or virtually to accommodate global teams.
            </p>
            <TrainingGrid>
              {trainingPrograms.map((program, index) => (
                <TrainingCard key={index}>
                  <TrainingIcon>{program.icon}</TrainingIcon>
                  <TrainingTitle>{program.title}</TrainingTitle>
                  <TrainingDescription>{program.description}</TrainingDescription>
                </TrainingCard>
              ))}
            </TrainingGrid>
          </BlockContent>
        </ContentBlock>

        <CTAButton
          onClick={() => {
            document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Connect with Our International Team
        </CTAButton>
      </Container>
    </InternationalSection>
  );
};

export default International;
